import React, { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { CheckCircle2, AlertTriangle, XCircle, ExternalLink, RefreshCcw, Scale } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const SERVICE_LABELS = {
    home_care: "Non-Skilled Home Care",
    b2b:       "B2B Facility Scouting",
    staffing:  "Travel / Per-Diem Staffing",
};

const STATUS_UI = {
    ok:      { icon: CheckCircle2, klass: "text-emerald-700 bg-emerald-100 border-emerald-300", label: "OK" },
    warn:    { icon: AlertTriangle, klass: "text-amber-700 bg-amber-100 border-amber-300",       label: "WATCH" },
    blocked: { icon: XCircle,       klass: "text-red-700 bg-red-100 border-red-300",             label: "BLOCKED" },
};

export default function CompliancePanel() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [openState, setOpenState] = useState(null);
    const [savingKey, setSavingKey] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get("/admin/state-compliance/matrix");
            setData(res.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load compliance matrix");
        } finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const toggle = async (state, service, itemIndex, completed) => {
        const key = `${state}-${service}-${itemIndex}`;
        setSavingKey(key);
        try {
            await api.post(`/admin/state-compliance/${state}/${service}/${itemIndex}/toggle`, { completed });
            // Optimistic update
            setData((d) => {
                if (!d) return d;
                const next = structuredClone(d);
                const st = next.states.find((s) => s.state === state);
                const svc = st?.services?.[service];
                if (svc) svc.requirements[itemIndex].completed = completed;
                return next;
            });
            if (openState?.state === state) {
                setOpenState((o) => ({ ...o, services: { ...o.services, [service]: { ...o.services[service], requirements: o.services[service].requirements.map((r, i) => i === itemIndex ? { ...r, completed } : r) } } }));
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Toggle failed");
        } finally { setSavingKey(null); }
    };

    if (loading && !data) return <div className="rounded-2xl border border-stone-200 bg-white p-5 text-xs text-stone-500 font-dmsans">Loading compliance matrix…</div>;
    if (!data) return null;

    const badge = (status) => {
        const s = STATUS_UI[status] || STATUS_UI.blocked;
        return <span className={`inline-flex items-center gap-1 rounded-full border ${s.klass} px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider`}>{s.label}</span>;
    };

    return (
        <div className="space-y-4" data-testid="compliance-panel">
            <div className="rounded-2xl border-2 border-stone-200 bg-gradient-to-br from-stone-50 to-white p-5">
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                    <Scale className="w-5 h-5 text-stone-700" />
                    <div className="font-outfit font-semibold text-stone-900">Multi-state compliance</div>
                    <Badge className="bg-emerald-700 text-white rounded-full text-[10px]">{data.counts.ok} clear</Badge>
                    <Badge className="bg-amber-700 text-white rounded-full text-[10px]">{data.counts.warn} watch</Badge>
                    <Badge className="bg-red-700 text-white rounded-full text-[10px]">{data.counts.blocked} blocked</Badge>
                    <Button size="sm" variant="ghost" className="ml-auto h-8 text-xs" onClick={load} disabled={loading} data-testid="compliance-refresh-btn">
                        <RefreshCcw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
                    </Button>
                </div>
                <p className="text-xs text-stone-600 font-dmsans mb-3">
                    Framework guidance — <strong>not legal advice</strong>. Verify each state with licensed counsel before operating.
                    Blocked states are greyed out on public signup pages.
                </p>
                <div className="grid grid-cols-4 sm:grid-cols-7 md:grid-cols-10 gap-2">
                    {data.states.map((s) => {
                        const ui = STATUS_UI[s.overall_status] || STATUS_UI.blocked;
                        return (
                            <button
                                key={s.state}
                                onClick={() => setOpenState(s)}
                                data-testid={`compliance-state-${s.state}`}
                                data-status={s.overall_status}
                                className={`aspect-square rounded-lg border-2 flex flex-col items-center justify-center p-1 hover:shadow-sm transition-shadow ${ui.klass}`}
                            >
                                <div className="font-outfit font-bold text-sm">{s.state}</div>
                                {s.overall_status === "ok"
                                    ? <CheckCircle2 className="w-3.5 h-3.5" />
                                    : s.overall_status === "warn"
                                        ? <AlertTriangle className="w-3.5 h-3.5" />
                                        : <XCircle className="w-3.5 h-3.5" />}
                            </button>
                        );
                    })}
                </div>
            </div>

            <Dialog open={!!openState} onOpenChange={(v) => !v && setOpenState(null)}>
                {openState && (
                    <DialogContent className="max-w-3xl rounded-2xl" data-testid="compliance-detail-dialog">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2 font-outfit">
                                {openState.state}
                                {badge(openState.overall_status)}
                            </DialogTitle>
                            <DialogDescription className="text-stone-600 font-dmsans">
                                Compliance requirements for each service line. Check items off as you complete them.
                            </DialogDescription>
                        </DialogHeader>

                        <div className="space-y-4 max-h-[65vh] overflow-y-auto">
                            {Object.entries(openState.services).map(([svcKey, svc]) => (
                                <div key={svcKey} className="rounded-xl border border-stone-200 p-4 bg-white">
                                    <div className="flex items-center gap-2 mb-2">
                                        <div className="font-outfit font-semibold text-stone-900 text-sm">{SERVICE_LABELS[svcKey] || svcKey}</div>
                                        {badge(svc.status)}
                                    </div>
                                    <p className="text-xs text-stone-700 font-dmsans mb-3">{svc.summary}</p>
                                    <ul className="space-y-2">
                                        {svc.requirements.map((r) => {
                                            const key = `${openState.state}-${svcKey}-${r.index}`;
                                            return (
                                                <li key={r.index} className={`flex items-start gap-2 p-2 rounded-lg border ${r.completed ? "border-emerald-200 bg-emerald-50" : "border-stone-200 bg-stone-50"}`}>
                                                    <input
                                                        type="checkbox"
                                                        checked={r.completed}
                                                        onChange={(e) => toggle(openState.state, svcKey, r.index, e.target.checked)}
                                                        disabled={savingKey === key}
                                                        data-testid={`compliance-item-${key}`}
                                                        className="mt-1 shrink-0"
                                                    />
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-1 flex-wrap">
                                                            <span className="text-[10px] uppercase tracking-wide bg-stone-200 text-stone-700 rounded-full px-1.5">{r.type}</span>
                                                            <span className="font-outfit font-medium text-stone-900 text-sm">{r.name}</span>
                                                            {r.url && (
                                                                <a href={r.url} target="_blank" rel="noreferrer" className="text-emerald-700 hover:text-emerald-800">
                                                                    <ExternalLink className="w-3 h-3 inline" />
                                                                </a>
                                                            )}
                                                        </div>
                                                        <div className="text-[11px] text-stone-600 font-dmsans mt-0.5 flex flex-wrap gap-x-3">
                                                            {r.cost && <span>Cost: {r.cost}</span>}
                                                            {r.timeline && <span>Timeline: {r.timeline}</span>}
                                                            {r.amount && <span>Amount: {r.amount}</span>}
                                                        </div>
                                                        {r.notes && <p className="text-[11px] text-stone-500 font-dmsans mt-1 italic">{r.notes}</p>}
                                                    </div>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    </DialogContent>
                )}
            </Dialog>
        </div>
    );
}
